import { ChangeEvent, useEffect, useState, useRef } from 'react';
import dynamic from 'next/dynamic';
import { FormikProps } from 'formik';
import { AdFormValues } from './types';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import useStyles from './styles';
import {
  ImageUploaderPropTypes,
  ImageUploaderVariant,
} from 'molecules/ImageUploader/ImageUploader';
import { CampaignType } from 'types/campaign';
import { TextField, RadioGroup, formatMoney } from '@elevator/ui';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import ActionButtons from 'molecules/ActionButtons';
import Typography from '@mui/material/Typography';
import { OriginalAdDesignCost } from 'config/Ads';

const DatePicker = dynamic(() =>
  import(/* webpackChunkName: "LocationAutocomplete" */ '@elevator/ui').then(
    (mod) => mod.DatePicker,
  ),
);

// NOTE ImageUploader implements Blob which is not defined on server
// this was not an issue on otis because most buttons aren't rendered via initial state
const ImageUploaderNoSsr = dynamic<ImageUploaderPropTypes<ImageUploaderVariant>>(
  () => import('../../molecules/ImageUploader'),
  { ssr: false },
);

const startDateOptions = ['Soonest Available', 'Choose Date'];
const designProvidedOptions = [`I'll Provide Ad Files`, `Ad Design by ELEVATOR +$150`];

const ImageAssetsDescription = ({ designProvided }: { designProvided: boolean }) => {
  if (designProvided) {
    return (
      <Typography variant="body2" color="text.secondary" component="div">
        Please upload a .zip file containing the following ad sizes
        <Box component="ul" sx={{ m: 0, pl: 2.5, my: 0.5 }}>
          <Box component="li">970x250</Box>
          <Box component="li">300x600</Box>
          <Box component="li">300x250</Box>
          <Box component="li">300x50</Box>
        </Box>
        .png, .jpg, .gif, 1mb max size per file
      </Typography>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      Please upload a .zip file containing creative assets (photos, logos, cover art
      files, etc) to be used in the ad designs.
    </Typography>
  );
};

/**
 * Calculates the campaign cost based on campaign type and design provided status.
 *
 * @param campaignTypeId - The selected campaign type ID
 * @param designProvided - Whether the user is providing their own design
 * @param campaignTypes - Array of available campaign types
 * @returns The calculated cost in cents
 */
const calculateCampaignCost = (
  campaignTypeId: string,
  designProvided: boolean,
  campaignTypes: Array<CampaignType>,
): number => {
  const selectedType = campaignTypes.find((type) => type.typeId === campaignTypeId);
  if (!selectedType) {
    return campaignTypes[0].cost;
  }

  let cost = selectedType.cost;
  if (!designProvided) {
    cost += OriginalAdDesignCost;
  }

  return cost;
};

/**
 * Fields Component
 *
 * Renders the form fields for the AdForm component. Handles all user inputs
 * including campaign details, file uploads, and cost calculations.
 *
 * Features:
 * - Campaign name and type selection
 * - Start date selection (soonest available or custom date)
 * - Design source selection (user-provided or ELEVATOR-designed)
 * - File upload for ad assets
 * - Click-through URL input
 * - Dynamic cost calculation and display
 * - Form validation and error handling
 *
 * @param props - Component props (Formik props + custom props)
 */
const Fields = (props: FieldsProps) => {
  const { classes } = useStyles();
  const {
    values,
    handleChange,
    setFieldValue,
    touched,
    setFieldTouched,
    errors,
    handleSubmit,
    onBackClick,
    isSubmitting,
    isValid,
    validateForm,
    campaignTypes,
    totalCost,
    updateTotalCost,
    stickyActionsFooter = false,
    showTotal = false,
  } = props;

  const theme = useTheme();

  useEffect(() => {
    validateForm();
  }, [validateForm]);

  const fileInputField = useRef<HTMLInputElement>(null);

  const [startDateMode, setStartDateMode] = useState('Soonest Available');

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldName: string,
  ): void => {
    setFieldTouched(fieldName, true);
    handleChange(event);
  };

  const handleSelectChange = (
    event: SelectChangeEvent<string>,
    fieldName: string,
  ): void => {
    setFieldTouched(fieldName, true);
    // Convert SelectChangeEvent to a format that Formik's handleChange can use
    handleChange({
      target: {
        name: fieldName,
        value: event.target.value,
      },
    } as ChangeEvent<HTMLInputElement>);
  };

  const handleStartDateToggle = (_e: ChangeEvent, value: string) => {
    if (value === 'Soonest Available') setFieldValue('startDate', 'default');
    else setFieldValue('startDate', dayjs());

    setStartDateMode(value);
  };

  const handleDesignProvided = (_e: ChangeEvent, value: string) => {
    const designProvided = value === designProvidedOptions[0];
    const newCost = calculateCampaignCost(
      values.campaignType,
      designProvided,
      campaignTypes,
    );

    updateTotalCost(newCost);
    setFieldValue('designProvided', designProvided, true);
  };

  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      className={stickyActionsFooter ? classes.stickyForm : undefined}
    >
      <Stack
        direction="column"
        spacing={2}
        className={stickyActionsFooter ? classes.stickyFormBody : undefined}
      >
        <TextField
          id={'campaignName'}
          name={'campaignName'}
          label={'Campaign Name'}
          fullWidth
          required
          value={values.campaignName}
          onChange={(e) => handleInputChange(e, 'campaignName')}
          error={touched.campaignName && Boolean(errors.campaignName)}
          helperText={touched.campaignName ? errors.campaignName : ''}
        />
        <FormControl
          fullWidth
          variant="filled"
          error={touched.campaignType && Boolean(errors.campaignType)}
        >
          <InputLabel id="campaign-type-label" shrink>
            Campaign Length
          </InputLabel>
          <Select
            id={'campaignType'}
            name={'campaignType'}
            labelId="campaign-type-label"
            label="Campaign Length"
            value={values.campaignType || ''}
            onChange={(e) => handleSelectChange(e, 'campaignType')}
            variant="filled"
            disableUnderline
            displayEmpty
            MenuProps={{
              PaperProps: {
                classes: { root: classes.dropdownPaper },
              },
            }}
          >
            {campaignTypes.map(({ typeId, name }) => (
              <MenuItem key={typeId} value={typeId}>
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          id={'clickThroughUrl'}
          name={'clickThroughUrl'}
          label={'Click-Through URL'}
          placeholder={'https://'}
          fullWidth
          required
          value={values.clickThroughUrl}
          onChange={(e) => handleInputChange(e, 'clickThroughUrl')}
          error={touched.clickThroughUrl && Boolean(errors.clickThroughUrl)}
          helperText={
            touched.clickThroughUrl && errors.clickThroughUrl
              ? errors.clickThroughUrl
              : 'Please provide url destination for ad clicks'
          }
        />
        <div>
          <RadioGroup
            label={'Campaign Start Date'}
            name={'startDate'}
            ariaLabel={'Campaign Start Date'}
            selectedValue={startDateMode}
            handleChange={handleStartDateToggle}
            values={startDateOptions}
          />
          {values.startDate !== 'default' && (
            <DatePicker
              pickerType="date"
              dateValue={values.startDate}
              onDateChange={(newDate) => {
                setFieldValue('startDate', newDate);
                setFieldTouched('startDate', true);
              }}
            />
          )}
        </div>
        <RadioGroup
          label={'Add Design'}
          name={'designProvided'}
          ariaLabel={'Add Design'}
          selectedValue={
            values.designProvided ? designProvidedOptions[0] : designProvidedOptions[1]
          }
          handleChange={handleDesignProvided}
          values={designProvidedOptions}
        />
        <Stack spacing={1}>
          <Typography variant="subtitle2">Image asset(s) *</Typography>
          <ImageAssetsDescription designProvided={values.designProvided} />
          <ImageUploaderNoSsr
            renderLabel={false}
            label={'Image asset(s) *'}
            error={touched.files && Boolean(errors.files)}
            helperText={touched.files && errors.files ? errors.files.toString() : ''}
            variant={'multiple'}
            inputProps={{
              id: 'files',
              // allow .zip and image files
              accept: 'zip,image/*',
              ref: fileInputField,
              onChange: (event: any) => {
                setFieldTouched('files', true, false); // set as touched, but don't validate per next line
                setFieldValue('files', new Array(...event.target.files));
              },
            }}
            onRemoveImage={(index: number) => {
              setFieldValue(
                'files',
                values.files.filter((_, i) => i !== index),
              );
            }}
            files={values.files}
          />
        </Stack>
        {showTotal && (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">Total</Typography>
            <Typography variant="h5">{formatMoney(totalCost / 100)}</Typography>
          </Stack>
        )}
        {!stickyActionsFooter && (
          <ActionButtons
            cancel={{ label: 'Back', onClick: () => onBackClick() }}
            submit={{
              label: 'Next',
              onClick: () => handleSubmit(),
              disabled: !isValid || isSubmitting,
            }}
          />
        )}
      </Stack>
      {stickyActionsFooter && (
        <Box className={classes.stickyActionsFooter}>
          <ActionButtons
            cancel={{ label: 'Back', onClick: () => onBackClick() }}
            submit={{
              label: 'Next',
              onClick: () => handleSubmit(),
              disabled: !isValid || isSubmitting,
            }}
          />
        </Box>
      )}
    </form>
  );
};

export type FieldsProps = FormikProps<AdFormValues> & {
  onBackClick: Function;
  campaignTypes: Array<CampaignType>;
  totalCost: number;
  updateTotalCost: (campaignCost: number) => void;
  stickyActionsFooter?: boolean;
  showTotal?: boolean;
};

export default Fields;
